import { gql } from '@apollo/client';
import {
  Avatar,
  HStack,
  IconButton,
  LinkBox,
  LinkOverlay,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Show,
  Text,
  VStack,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { DocumentNode } from 'graphql';
import NextLink from 'next/link';
import * as React from 'react';

import {
  DownloadIcon,
  Edit2Icon,
  EyeIcon,
  MessageCircleIcon,
  MoreHorizontalIcon,
  MoveIcon,
  TrashIcon,
} from '@/imports/ui/chakra/feather';

import { FileItemDropdown_VersionFileFragment } from './graphql-types/FileItemDropdown_VersionFileFragment';

type IVersionFile = FileItemDropdown_VersionFileFragment;

export const FileItemDropdown: React.FC<{
  versionFile: IVersionFile;
  previewDisabled: boolean;
  canEditGroupLibrary: boolean;
  onOpenPreviewFile: () => void;
  onOpenMoveFile: () => void;
  onOpenDeleteFile: () => void;
  onOpenRenameFile: () => void;
}> & { versionFileFragment: DocumentNode } = ({
  versionFile,
  previewDisabled,
  canEditGroupLibrary,
  onOpenPreviewFile,
  onOpenMoveFile,
  onOpenDeleteFile,
  onOpenRenameFile,
}) => {
  if (!versionFile?.file) return null;

  return (
    <Menu aria-label="file actions">
      <MenuButton
        as={IconButton}
        variant="outline"
        colorScheme="gray"
        icon={<MoreHorizontalIcon />}
        size="sm"
      />
      <Portal>
        <MenuList>
          <MenuItem
            icon={<EyeIcon color="gray.600" />}
            onClick={onOpenPreviewFile}
            isDisabled={previewDisabled}
          >
            {previewDisabled ? 'Preview Not Available' : 'Preview'}
          </MenuItem>

          {versionFile.originalPost?._id && (
            <NextLink href={`/post/${versionFile.originalPost._id}`}>
              <MenuItem icon={<MessageCircleIcon color="gray.600" />}>
                See Original Post
              </MenuItem>
            </NextLink>
          )}

          <MenuItem>
            <LinkBox width="full">
              <HStack width="full">
                <DownloadIcon color="gray.600" />

                <LinkOverlay
                  download={versionFile.file.name}
                  href={versionFile.file.url}
                  flex="1"
                  _hover={{ textDecoration: 'unset' }}
                  color="gray.700"
                >
                  <Text as="span" fontWeight="semibold">
                    Download
                  </Text>
                </LinkOverlay>
              </HStack>
            </LinkBox>
          </MenuItem>
          {canEditGroupLibrary && (
            <>
              <MenuItem
                icon={<MoveIcon color="gray.600" />}
                onClick={onOpenMoveFile}
              >
                Move
              </MenuItem>
              <MenuItem
                icon={<TrashIcon color="gray.600" />}
                onClick={onOpenDeleteFile}
              >
                Delete
              </MenuItem>
              <MenuItem
                icon={<Edit2Icon color="gray.600" />}
                onClick={onOpenRenameFile}
              >
                Rename
              </MenuItem>
            </>
          )}
          <Show below="lg">
            <MenuDivider />
            <NextLink href={`/members/${versionFile.author._id}`}>
              <HStack pl="2" pr="2">
                <Avatar
                  size="sm"
                  name={versionFile.author.displayName}
                  src={versionFile.author.proxyProfilePictureUrl || undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                />
                <VStack align="left">
                  <Text textStyle="body" lineHeight="4">
                    Created{' '}
                    {dayjs(versionFile.dateCreated).format('MMM D, YYYY')} by
                  </Text>
                  <Text textStyle="bodyStrong" lineHeight="4">
                    {versionFile.author.displayName}
                  </Text>
                </VStack>
              </HStack>
            </NextLink>
          </Show>
        </MenuList>
      </Portal>
    </Menu>
  );
};

FileItemDropdown.versionFileFragment = gql`
  fragment FileItemDropdown_VersionFileFragment on VersionFile {
    file {
      name
      url
    }
    dateCreated
    originalPost {
      _id
    }
    author {
      _id
      displayName
      proxyProfilePictureUrl(
        input: { w: 40, h: 40, fit: "facearea", facepad: 10.0 }
      )
    }
  }
`;
